import {
    Component, OnInit, OnDestroy,
    ViewChild, ElementRef, WritableSignal, signal,
    numberAttribute, inject
} from '@angular/core';
import {
    FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';

import { MatterService } from '../shared/services/matter.service';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { CommonService } from '../shared/services/common.service';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserService } from '../shared/services/user.service';
import { BankAccountService } from '../shared/services/bank-account.service';

import { Matter } from '../shared/models/matter';
import { AddExpectFundDto } from '../shared/models/expectFundRequestDTO';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { Authoriser } from '../shared/models/authoriser';
import { BankAccount } from '../shared/models/bankAccount';
import { RequestType } from '../enums/request-type.enum';
import { Currency } from '../shared/models/currency';
import { DraftEditor, DraftSaveOutcome } from '../shared/draft-editor/draft-editor';
import { DraftSnapshot, readBoolean, readDate, readNumber, readString } from '../shared/draft-editor/draft-snapshot';

const PM_EFT = 'Electronic funds transfer';
const PM_CHEQUE = 'Cheque';
const PM_CARD = 'Credit Card';

@Component({
    selector: 'app-expect-fund',
    templateUrl: './expect-fund.component.html',
    styleUrl: './expect-fund.component.scss',
    providers: [DraftEditor]
})
export class ExpectFundComponent implements OnInit, OnDestroy {

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    /** Saves this form as a draft and reopens saved drafts. */
    readonly draftEditor = inject(DraftEditor);
    private readonly route = inject(ActivatedRoute);

    currentDate = new Date();
    expectFundForm!: FormGroup;

    isOfficeReceipt = false;

    isUserPartner: boolean | null = null;
    isLoading = false;
    isPayerReadOnly = false;
    noData = false;
    submitBtn = false;
    gbpCurrencyId = 0;

    matterDropdownData: Matter[] = [];
    clientOptions: string[] = [];
    readonly payerClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payerErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payer = this.expectFundForm?.get('payerName');
            return !!payer && payer.touched && payer.invalid;
        }
    };
    partnerList: Authoriser[] = [];
    bankAccounts: BankAccount[] = [];
    filteredBankAccounts: BankAccount[] = [];
    currencyDropdown: Currency[] = [];

    readonly paymentMethodDropdown = [PM_EFT, PM_CHEQUE];

    displayFile: string[] = [];
    fileAttachments: AttachmentData[] = [];
    matterNo = '';
    matterDesc = '';

    actionstepMatterUrl = '';
    actionstepMatterOverviewUrl = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);

    readonly PM_CHEQUE = PM_CHEQUE;
    readonly PM_CARD = PM_CARD;

    private readonly destroy$ = new Subject<void>();
    private actionstepBaseUrl = '';
    private actionstepOverviewBaseUrl = '';

    constructor(
        private fb: FormBuilder,
        private matterService: MatterService,
        private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private commonService: CommonService,
        private userService: UserService,
        private manageUserRoleService: ManageUserRoleService,
        private bankAccountService: BankAccountService
    ) { }

    async ngOnInit(): Promise<void> {
        this.draftEditor.configure(RequestType.ExpectFunds);
        this.buildForm();
        this.draftEditor.watchForm(() => ({ snapshot: this.buildDraftSnapshot(), pendingFiles: this.fileAttachments.length }));
        this.setupFormListeners();
        await this.loadInitialData();
        // Before the matter search is wired up, so restoring the matter does not trigger a search.
        await this.restoreDraftFromRoute();
        this.setupMatterAutocomplete();
        this.draftEditor.markClean();
    }

    /** Whether the form holds changes that have not been saved. Used to warn the user before they leave. */
    hasUnsavedChanges(): boolean {
        return this.draftEditor.hasUnsavedChanges();
    }

    /** True when there is something worth saving as a draft: the user has typed something, or this is a saved draft. */
    get canSaveDraft(): boolean {
        return this.expectFundForm.dirty || this.fileAttachments.length > 0 || this.draftEditor.draftId() !== null;
    }

    /**
     * Saves the form as a draft, however incomplete it is.
     * @returns True only when the draft and all its new documents were saved.
     */
    async saveDraft(): Promise<boolean> {
        const f = this.expectFundForm.getRawValue();
        const amount = Number(String(f.expectedAmount ?? '').replace(/,/g, ''));

        const outcome = await this.draftEditor.save({
            snapshot: this.buildDraftSnapshot(),
            summary: {
                matterNo: Number(this.matterNo) || null,
                matter: this.matterDesc || null,
                clientName: f.clientName || null,
                amount: f.expectedAmount && Number.isFinite(amount) ? amount : null,
                reason: f.paymentReason || null
            },
            newFiles: [...this.fileAttachments]
        });

        if (outcome === DraftSaveOutcome.Saved) {
            this.clearUpload();
        }
        return outcome === DraftSaveOutcome.Saved;
    }

    /**
     * Handles the Save as Draft button: saves the draft and, once everything is saved, clears the form for the next request,
     * as after a submit. If anything could not be saved, the form stays as it is.
     * @param formDirective The form's directive, so the form is reset together with its submitted state.
     */
    async saveDraftAndReset(formDirective: FormGroupDirective): Promise<void> {
        if (!await this.saveDraft()) return;

        this.resetForm(formDirective);
        await this.draftEditor.afterDraftSaved();
    }

    private async restoreDraftFromRoute(): Promise<void> {
        const draftId = Number(this.route.snapshot.paramMap.get('draftId'));
        if (!Number.isInteger(draftId) || draftId <= 0) return;

        const snapshot = await this.draftEditor.open(draftId);
        if (snapshot) {
            await this.applyDraftSnapshot(snapshot);
        }
    }

    private buildDraftSnapshot(): DraftSnapshot {
        const f = this.expectFundForm.getRawValue();
        const bankAccount = f.clientBankAccount as BankAccount | '' | null;

        return {
            values: {
                matter: typeof f.matter === 'string' ? f.matter : '',
                clientName: f.clientName ?? '',
                paymentMethod: f.paymentMethod ?? '',
                paymentReason: f.paymentReason ?? '',
                expectedAmount: f.expectedAmount ?? '',
                payerName: f.payerName ?? '',
                notesToAccounts: f.notesToAccounts ?? '',
                moniesReturnedClient: f.moniesReturnedClient === true,
                clientBankAccountId: bankAccount ? bankAccount.accountId : '',
                partnerAuthorisingId: f.partnerAuthorisingId ?? '',
                isPartnerCertified: f.isPartnerCertified === true,
                checkDate: f.checkDate instanceof Date ? f.checkDate.toISOString() : null,
                checkFrom: f.checkFrom ?? '',
                checkNumber: f.checkNumber ?? '',
                checkMemo: f.checkMemo ?? '',
                checkBankName: f.checkBankName ?? '',
                checkBankBranch: f.checkBankBranch ?? '',
                lastFourNumbers: f.lastFourNumbers ?? '',
                authorizationNumber: f.authorizationNumber ?? ''
            },
            state: { matterNo: this.matterNo, matterDesc: this.matterDesc }
        };
    }

    private async applyDraftSnapshot(snapshot: DraftSnapshot): Promise<void> {
        const { values, state } = snapshot;

        this.matterNo = readString(state, 'matterNo');
        this.matterDesc = readString(state, 'matterDesc');
        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        // The matter first: choosing one is what enables the payer fields patched below.
        this.expectFundForm.patchValue({ matter: readString(values, 'matter'), matterNumber: this.matterNo });

        // Quietly, so restoring "monies returned to client" does not overwrite the saved payer name.
        const bankAccountId = readString(values, 'clientBankAccountId');
        const partnerId = readNumber(values, 'partnerAuthorisingId');
        this.expectFundForm.patchValue({
            clientName: readString(values, 'clientName'),
            paymentMethod: readString(values, 'paymentMethod'),
            paymentReason: readString(values, 'paymentReason'),
            expectedAmount: readString(values, 'expectedAmount'),
            payerName: readString(values, 'payerName'),
            notesToAccounts: readString(values, 'notesToAccounts'),
            moniesReturnedClient: readBoolean(values, 'moniesReturnedClient'),
            clientBankAccount: this.bankAccounts.find(account => account.accountId === bankAccountId) ?? '',
            // A saved partner who has since lost that role is no longer in the list; the user must choose again.
            partnerAuthorisingId: this.partnerList.some(partner => partner.id === partnerId) ? partnerId : '',
            isPartnerCertified: readBoolean(values, 'isPartnerCertified'),
            checkDate: readDate(values, 'checkDate'),
            checkFrom: readString(values, 'checkFrom'),
            checkNumber: readString(values, 'checkNumber'),
            checkMemo: readString(values, 'checkMemo'),
            checkBankName: readString(values, 'checkBankName'),
            checkBankBranch: readString(values, 'checkBankBranch'),
            lastFourNumbers: readString(values, 'lastFourNumbers'),
            authorizationNumber: readString(values, 'authorizationNumber')
        }, { emitEvent: false });

        this.isPayerReadOnly = readBoolean(values, 'moniesReturnedClient');

        if (this.matterNo) {
            await this.loadClientDetails(Number(this.matterNo));
            const savedPayer = `, ${readString(values, 'payerName')}, `;
            this.payerClients.setValue(this.clientOptions.filter(name => savedPayer.includes(`, ${name}, `)));
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildForm(): void {
        this.expectFundForm = this.fb.group({
            matter: ['', Validators.required],
            matterNumber: [''],
            clientName: [''],

            paymentMethod: ['', Validators.required],
            paymentReason: ['', Validators.required],
            expectedAmount: ['', [Validators.required, Validators.pattern(/^[0-9.,]*$/)]],
            payerName: ['', Validators.required],
            notesToAccounts: [''],
            moniesReturnedClient: [false, Validators.required],
            clientBankAccount: ['', Validators.required],
            clientOffice: [''],

            dateRequested: [new Date(), Validators.required],
            requestedTime: [this.toTimeString(new Date()), Validators.required],

            partnerAuthorisingId: [''],
            currencyId: '',

            isPartnerCertified: [false],

            checkDate: [null],
            checkFrom: [''],
            checkNumber: [''],
            checkMemo: [''],
            checkBankName: [''],
            checkBankBranch: [''],

            lastFourNumbers: [''],
            authorizationNumber: [''],

            uploadedFiles: [[]]
        });
    }

    private setupFormListeners(): void {
        this.expectFundForm.get('moniesReturnedClient')?.disable();
        this.expectFundForm.get('payerName')?.disable();

        this.expectFundForm.get('matter')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                const hasValue = !!val;
                if (hasValue) {
                    this.expectFundForm.get('moniesReturnedClient')?.enable();
                    this.expectFundForm.get('payerName')?.enable();
                } else {
                    this.expectFundForm.get('moniesReturnedClient')?.setValue(false);
                    this.expectFundForm.get('moniesReturnedClient')?.disable();
                    this.expectFundForm.get('payerName')?.disable();
                }
            });

        this.expectFundForm.get('moniesReturnedClient')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                this.isPayerReadOnly = val === true;
                if (val === true) {
                    this.loadClientNameIntoPayerName();
                } else {
                    this.expectFundForm.get('payerName')?.reset();
                    this.payerClients.setValue([]);
                }
            });

        }

    private updatePaymentMethodValidators(): void {
        const allConditionalFields = ['checkDate', 'checkFrom', 'checkNumber', 'checkBankName', 'checkBankBranch', 'lastFourNumbers', 'authorizationNumber'];
        allConditionalFields.forEach(f => this.clearField(f));
    }

    private updatePartnerAuthorisingValidator(): void {
        const authCtrl = this.expectFundForm.get('partnerAuthorisingId');
        const certifiedCtrl = this.expectFundForm.get('isPartnerCertified');

        if (this.isUserPartner) {
            authCtrl?.clearValidators();
            certifiedCtrl?.setValidators(Validators.requiredTrue);
        } else {
            authCtrl?.setValidators(Validators.required);
            certifiedCtrl?.clearValidators();
        }

        authCtrl?.updateValueAndValidity({ emitEvent: false });
        certifiedCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    private setRequired(controlName: string): void {
        const ctrl = this.expectFundForm.get(controlName);
        if (!ctrl) return;
        ctrl.setValidators(Validators.required);
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private clearField(controlName: string): void {
        const ctrl = this.expectFundForm.get(controlName);
        if (!ctrl) return;
        ctrl.clearValidators();
        ctrl.setErrors(null);
        ctrl.markAsUntouched();
        ctrl.markAsPristine();
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private async loadInitialData(): Promise<void> {
        try {
            await Promise.all([this.loadUserRole(), this.loadDropdownData()]);
        } catch {
            this.snackbarService.errorSnackBar('Failed to load initial data');
        }
    }

    private async loadUserRole(): Promise<void> {
        const role = await this.manageUserRoleService.getUserPermissionsByRole(this.userService.userDetails?.userRole!);
        this.isUserPartner = role.partnerApprove;

        this.updatePartnerAuthorisingValidator();
    }

    private async loadDropdownData(): Promise<void> {
        const [bankAccounts, partners, actionstepUrl, actionstepOverviewUrl, matters, currency] = await Promise.all([
            this.bankAccountService.getAllBankAccounts(),
            this.requestService.getPartnersList(),
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
            this.matterService.getMatterList('', ''),
            this.requestService.getAllCurrencies()
        ]);

        this.bankAccounts = bankAccounts;
        this.filteredBankAccounts = [...bankAccounts];
        this.partnerList = partners;
        this.actionstepBaseUrl = actionstepUrl;
        this.actionstepOverviewBaseUrl = actionstepOverviewUrl;
        this.matterDropdownData = matters;
        this.currencyDropdown = currency;

        this.gbpCurrencyId = this.currencyDropdown.find(c => c.currencyCode === 'GBP')?.id ?? 0;

        this.expectFundForm.get('currencyId')?.setValue(this.gbpCurrencyId, {
            emitEvent: false
        });
    }

    get showPayerSelect(): boolean {
        return this.clientOptions.length > 1
            && this.expectFundForm.get('moniesReturnedClient')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.expectFundForm.get('payerName')?.value ?? '';
        const ticked = this.payerClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    private async loadClientNameIntoPayerName(): Promise<void> {
        if (!this.matterNo) return;
        if (!this.clientOptions.length && !await this.loadClientDetails(Number(this.matterNo))) {
            this.expectFundForm.get('payerName')?.setValue('Error loading client');
            return;
        }

        if (this.clientOptions.length > 1) {
            this.onPayerClientsChange();
        } else {
            this.expectFundForm.get('payerName')?.setValue(this.clientOptions[0] ?? 'Client name not found');
        }
    }

    onPayerClientsChange(): void {
        const ticked = this.payerClients.value;
        this.expectFundForm.get('payerName')?.setValue(this.clientOptions.filter(name => ticked.includes(name)).join(', '));
    }

    onPayerSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.expectFundForm.get('payerName')?.markAsTouched();
    }

    private clearClients(): void {
        this.clientOptions = [];
        this.payerClients.setValue([]);
    }

    private setupMatterAutocomplete(): void {
        this.expectFundForm.get('matter')!.valueChanges.pipe(
            debounceTime(300),
            switchMap(query => {
                const isNumeric = !isNaN(Number(query));
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(
                    isNumeric ? (query ?? '') : '',
                    isNumeric ? '' : (query ?? '')
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: data => {
                this.matterDropdownData = data ?? [];
                this.noData = !data?.length;
                this.isLoading = false;
            },
            error: err => {
                this.noData = true;
                this.isLoading = false;
                if (err.status === 401) this.commonService.apiSecurityError();
            }
        });
    }

    async lookupSelectionChanged(event: MatOptionSelectionChange<string>): Promise<void> {
        if (!event.isUserInput) return;
        const raw = event.source.viewValue;
        if (!raw?.includes(' - ')) return;

        [this.matterNo, this.matterDesc] = raw.split(' - ');

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.expectFundForm.patchValue({
            matter: `${this.matterNo} - ${this.matterDesc}`,
            matterNumber: this.matterNo,
            clientName: ''
        });

        this.clearClients();
        await this.loadClientDetails(Number(this.matterNo));
        if (this.expectFundForm.get('moniesReturnedClient')?.value === true) {
            this.loadClientNameIntoPayerName();
        }
    }

    /**
     * Loads the matter's clients: all of them, joined, into the Client Name field, and as the options to tick from
     * when the payer is chosen from a list.
     * @returns False when the clients could not be loaded.
     */
    private async loadClientDetails(matterNumber: number): Promise<boolean> {
        const clientCtrl = this.expectFundForm.get('clientName');
        this.isLoading = true;
        try {
            this.clientOptions = await this.matterService.getMatterClientNames(matterNumber);
            clientCtrl?.setValue(this.clientOptions.length ? this.clientOptions.join(', ') : 'No client found');
            return true;
        } catch {
            this.clearClients();
            clientCtrl?.setValue('No client found');
            return false;
        } finally {
            this.isLoading = false;
        }
    }

    displayMatter(matter: Matter): string {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    async submit(formDirective: FormGroupDirective): Promise<void> {
        // The Enter key can submit even when the button is disabled; a draft that is still being saved must not be submitted.
        if (this.draftEditor.saving()) return;

        if (this.expectFundForm.invalid) {
            this.expectFundForm.markAllAsTouched();
            this.snackbarService.errorSnackBar('Please complete all required fields');
            return;
        }

        this.submitBtn = true;
        const f = this.expectFundForm.value;

        const cleanAmount = f.expectedAmount
            ? Number(String(f.expectedAmount).replace(/,/g, ''))
            : 0;

        const selectedPartner = this.partnerList.find(p => p.id === f.partnerAuthorisingId);

        const datePart: Date = f.dateRequested instanceof Date ? f.dateRequested : new Date(f.dateRequested);

        const [hours, minutes] = (f.requestedTime ?? '00:00').split(':').map(Number);

        const requestedDate = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, 0, 0);

        const dto: AddExpectFundDto = {
            draftId: this.draftEditor.draftId() ?? undefined,
            matterNo: Number(this.matterNo),
            matter: this.matterDesc,
            clientName: f.clientName ?? undefined,
            clientOffice: this.isOfficeReceipt ? 'Office' : 'Client',
            clientBankAccountName: f.clientBankAccount
                ? `${(f.clientBankAccount as BankAccount).accountId} - ${(f.clientBankAccount as BankAccount).description}`
                : undefined,
            clientBankAccountId: f.clientBankAccount ? (f.clientBankAccount as BankAccount).accountId : undefined,

            currencyId: f.currencyId ?? 0,
            partnerAuthorisingId: selectedPartner?.id,

            paymentMethod: f.paymentMethod ?? undefined,
            paymentReason: f.paymentReason ?? undefined,
            expectedAmount: cleanAmount,
            payerName: f.payerName ?? undefined,
            notesToAccounts: f.notesToAccounts || undefined,
            moniesReturnedClient: f.moniesReturnedClient ?? false,
            transactionReference: undefined,

            dateRequested: requestedDate,

            isPartnerCertified: f.isPartnerCertified ?? false,

            checkDate: f.checkDate ?? undefined,
            checkFrom: f.checkFrom || undefined,
            checkNumber: f.checkNumber || undefined,
            checkMemo: f.checkMemo || undefined,
            checkBankName: f.checkBankName || undefined,
            checkBankBranch: f.checkBankBranch || undefined,

            lastFourNumbers: f.lastFourNumbers || undefined,
            authorizationNumber: f.authorizationNumber || undefined,

            returnReason: undefined
        };

        let requestCreated = false;
        try {
            const res = await this.requestService.addExpectFund(dto);
            requestCreated = true;

            if (f.uploadedFiles?.length > 0) {
                const docData: RequestDocAttachmentResponse = {
                    requestId: res.id!,
                    requestType: RequestType.ExpectFunds,
                    attachments: f.uploadedFiles
                };
                await this.requestService.addRequestDocument(docData);
                this.clearUpload();
                this.snackbarService.successSnackBar('Documents uploaded successfully');
            }

            this.snackbarService.successSnackBar('Receipt request submitted successfully');
            this.resetForm(formDirective);
        } catch {
            this.snackbarService.errorSnackBar('Failed to submit receipt request. Please try again.');
        } finally {
            this.submitBtn = false;
            if (requestCreated) {
                // The draft (if any) was consumed when the request was created, whatever happened to the documents.
                await this.draftEditor.afterSubmit();
            }
        }
    }

    resetForm(formDirective?: FormGroupDirective): void {
        this.matterNo = '';
        this.matterDesc = '';
        this.clearClients();
        this.displayFile = [];
        this.fileAttachments = [];
        this.showLinkToOpenMatter.set(false);
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';

        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';

        const resetValues = {
            matter: '',
            matterNumber: '',
            clientName: '',
            paymentMethod: '',
            paymentReason: '',
            expectedAmount: '',
            payerName: '',
            notesToAccounts: '',
            moniesReturnedClient: false,
            clientBankAccount: '',
            dateRequested: new Date(),
            requestedTime: this.toTimeString(new Date()),
            partnerAuthorisingId: '',
            currencyId: this.gbpCurrencyId,
            isPartnerCertified: false,
            checkDate: null,
            checkFrom: '',
            checkNumber: '',
            checkMemo: '',
            checkBankName: '',
            checkBankBranch: '',
            lastFourNumbers: '',
            authorizationNumber: '',
            uploadedFiles: []
        };

        if (formDirective) {
            formDirective.resetForm(resetValues);
        } else {
            this.expectFundForm.reset(resetValues);
        }

        Object.values(this.expectFundForm.controls).forEach(ctrl => {
            ctrl.markAsPristine();
            ctrl.markAsUntouched();
            ctrl.setErrors(null);
            ctrl.updateValueAndValidity({ emitEvent: false });
        });

        this.expectFundForm.get('moniesReturnedClient')?.disable();
        this.expectFundForm.get('payerName')?.disable();

        this.updatePartnerAuthorisingValidator();
        this.updatePaymentMethodValidators();

        setTimeout(() => {
            this.expectFundForm.markAsPristine();
            this.expectFundForm.markAsUntouched();
            this.expectFundForm.updateValueAndValidity({ emitEvent: false });
        }, 0);
    }

    clearSearchFilter(): void {
        this.expectFundForm.get('matter')?.reset();
        this.expectFundForm.get('matterNumber')?.reset();
        this.clearClients();
        this.expectFundForm.get('clientName')?.setValue('');
        this.matterDropdownData = [];
        this.matterNo = '';
        this.matterDesc = '';
        this.showLinkToOpenMatter.set(false);
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';
    }


    onFilesSelected(input: HTMLInputElement): void {
        if (!input.files?.length) {
            this.expectFundForm.get('uploadedFiles')?.setValue([]);
            return;
        }

        this.fileAttachments = [];
        this.displayFile = [];

        Array.from(input.files).forEach(file => {
            this.displayFile.push(file.name);
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
                const result = e.target?.result as string;
                const bytes = result.split('base64,')[1];
                this.fileAttachments.push({ fileName: file.name, attachments: bytes });
                this.expectFundForm.get('uploadedFiles')?.setValue([...this.fileAttachments]);
            };
            reader.readAsDataURL(file);
        });
    }

    clearUpload(): void {
        this.displayFile = [];
        this.fileAttachments = [];
        this.expectFundForm.get('uploadedFiles')?.setValue([]);
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
    }

    validateInput(event: KeyboardEvent): void {
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(event.key)) return;
        if (!/[0-9.,]/.test(event.key)) event.preventDefault();
    }

    formatNumber(): void {
        const ctrl = this.expectFundForm.get('expectedAmount');
        const rawValue = ctrl?.value;
        if (!rawValue || rawValue === '' || rawValue === '0') {
            ctrl?.setValue('', { emitEvent: false });
            return;
        }
        const clean = String(rawValue).replace(/,/g, '');
        const formatted = this.commonService.formatNumberWithoutRounding(Number(clean));
        ctrl?.setValue(formatted, { emitEvent: false });
    }


    filterBankAccounts(): void {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    onUrlCopied(): void {
        this.snackbarService.successSnackBar('URL copied to clipboard');
    }

    get selectedPaymentMethod(): string {
        return this.expectFundForm.get('paymentMethod')?.value ?? '';
    }
}
